/**
 * `EvBar` — one component, four places (`docs/visit-page.md` §7.5): the day
 * tiles, the KPIs, Time and the Money tab's Hours.
 *
 * The track is `line-soft`; the **light** fill runs to *projected*
 * (`accent-ring`, or `warn-line` when projected is over the estimate); the
 * **solid** fill to *so far* (`accent`, or `warn-bar` when so far alone is
 * over); a 2 px ink **tick** at the estimate. Every position is the server's
 * (`EvBar` in `shared/src/visit-page/values.ts`) — the page draws, it never
 * scales.
 */
import type { BarPct, EvBar as EvBarData, MarginBar as MarginBarData } from '@rowted/shared'
import { cn } from '../lib/cn.js'

/** `sm` is the day tiles' 6 px bar (tick 12); `md` the KPIs' 8 px (tick 14). */
export type EvBarSize = 'sm' | 'md'

const TRACK: Record<EvBarSize, string> = { sm: 'h-1.5', md: 'h-2' }
const TICK: Record<EvBarSize, string> = { sm: 'h-3', md: 'h-3.5' }

export function EvBar({ bar, size = 'sm', className, tick = true }: {
  bar: EvBarData; size?: EvBarSize; className?: string
  /** No estimate (D23, §4.2) — nothing to tick. */
  tick?: boolean
}) {
  return (
    <div className={cn('relative rounded-full bg-line-soft', TRACK[size], className)} data-ev-bar="">
      <div
        className={cn('absolute inset-y-0 left-0 rounded-full', bar.projectedOver ? 'bg-warn-line' : 'bg-accent-ring')}
        style={{ width: `${bar.projectedPct}%` }}
        data-ev="projected"
      />
      <div
        className={cn('absolute inset-y-0 left-0 rounded-full', bar.soFarOver ? 'bg-warn-bar' : 'bg-accent')}
        style={{ width: `${bar.soFarPct}%` }}
        data-ev="so-far"
      />
      {tick && <Tick pct={bar.estimatePct} size={size} />}
    </div>
  )
}

/** The MARGIN KPI's bar (Boards 1, 13 A, 14): the margin as a green fill on a 0 – 100 track, the estimate as the tick. */
export function MarginBar({ bar, className, tick = true }: { bar: MarginBarData; className?: string; tick?: boolean }) {
  return (
    <div className={cn('relative h-2 rounded-full bg-line-soft', className)} data-ev-bar="">
      <div className="absolute inset-y-0 left-0 rounded-full bg-accent" style={{ width: `${bar.fillPct}%` }} />
      {tick && <Tick pct={bar.tickPct} size="md" />}
    </div>
  )
}

/** A plain progress bar — *5 of 7 ready* (Board 1's Ready for Friday, 120 × 6). */
export function ReadyBar({ pct, className }: { pct: BarPct; className?: string }) {
  return (
    <div className={cn('relative h-1.5 w-[120px] rounded-full bg-line-soft', className)}>
      <div className="h-full rounded-full bg-accent" style={{ width: `${pct}%` }} />
    </div>
  )
}

function Tick({ pct, size }: { pct: BarPct; size: EvBarSize }) {
  return (
    <i
      aria-hidden
      className={cn('absolute -top-[3px] block w-0.5 rounded-sm bg-text', TICK[size])}
      style={{ left: `calc(${pct}% - 1px)` }}
      data-ev="estimate"
    />
  )
}
